import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import ResetPasswordForm from './ResetPasswordForm';
import { safeParseJson, extractApiError } from './passwordValidation';

const ResetPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const validateToken = useCallback(async (token: string) => {
    setValidatingToken(true);

    try {
      const response = await fetch(
        `${BUILD_VARS.QUERY_DOMAIN}/v1.0/auth/reset-password/validate/${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const data = await safeParseJson(response);

      if (data?.success) {
        return {
          isValid: true,
          email: data?.data.response?._embedded?.user?.profile?.login,
          userId: data?.data.response?._embedded?.user?.id,
          stateToken: data?.data.response?.stateToken,
        };
      } else {
        return {
          isValid: false,
          error: data?.error || 'Invalid or expired password reset token',
        };
      }
    } catch (err) {
      return {
        isValid: false,
        error: 'Network error. Please check your connection and try again.',
      };
    } finally {
      setValidatingToken(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: { token: string; password: string; stateToken?: string }) => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const response = await fetch(
          `${BUILD_VARS.QUERY_DOMAIN}/v1.0/auth/reset-password/${values.token}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              password: values.password,
              stateToken: values.stateToken,
            }),
          },
        );

        if (response.ok) {
          const data = await safeParseJson(response);
          const successMessage =
            data?.message || 'Password reset successfully! You can now log in.';
          setSuccess(successMessage);
          message.success('Password reset successfully!');
        } else {
          const errorMessage = await extractApiError(
            response,
            'Failed to reset password. Please try again.',
          );
          setError(errorMessage);
          message.error(errorMessage);
        }
      } catch (err) {
        const errorMessage = 'Network error. Please check your connection and try again.';
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return (
    <ResetPasswordForm
      onSubmit={handleSubmit}
      onValidateToken={validateToken}
      loading={loading}
      validatingToken={validatingToken}
      error={error}
      success={success}
    />
  );
};

export default ResetPasswordPage;
